import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { PaginatedResponse } from '@/types'
import type {
  AccountingSummary,
  Expense,
  ExpenseFilters,
  ExpenseInput,
  ExpensesPage,
  IncomeReport,
  IncomeReportParams,
  TaxSettings,
  TaxSettingsInput,
} from '@/types/accounting'

type ApiResponse<T> = { message?: string; data?: T }

type IndexResponse<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

function unwrapPaginated<T>(raw: IndexResponse<T>): PaginatedResponse<T> {
  return {
    data: raw.data,
    current_page: raw.meta.current_page,
    last_page: raw.meta.last_page,
    per_page: raw.meta.per_page,
    total: raw.meta.total,
  }
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

export const accountingApi = {
  getTaxSettings: async (): Promise<TaxSettings> => {
    const response = await apiClient.get<ApiResponse<TaxSettings>>(API_ENDPOINTS.ACCOUNTING.TAX_SETTINGS)
    return unwrapResource(response.data)
  },

  updateTaxSettings: async (payload: TaxSettingsInput): Promise<TaxSettings> => {
    const response = await apiClient.patch<ApiResponse<TaxSettings>>(
      API_ENDPOINTS.ACCOUNTING.TAX_SETTINGS,
      payload,
    )
    return unwrapResource(response.data)
  },

  listExpenses: async (filters: ExpenseFilters = {}): Promise<ExpensesPage> => {
    const response = await apiClient.get<IndexResponse<Expense>>(API_ENDPOINTS.ACCOUNTING.EXPENSES, {
      params: filters,
    })
    return unwrapPaginated(response.data)
  },

  createExpense: async (payload: ExpenseInput): Promise<Expense> => {
    const response = await apiClient.post<ApiResponse<Expense>>(API_ENDPOINTS.ACCOUNTING.EXPENSES, payload)
    return unwrapResource(response.data)
  },

  updateExpense: async (id: number, payload: Partial<ExpenseInput>): Promise<Expense> => {
    const response = await apiClient.patch<ApiResponse<Expense>>(API_ENDPOINTS.ACCOUNTING.EXPENSE(id), payload)
    return unwrapResource(response.data)
  },

  deleteExpense: async (id: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ACCOUNTING.EXPENSE(id))
  },

  incomeReport: async (params: IncomeReportParams = {}): Promise<IncomeReport> => {
    const response = await apiClient.get<ApiResponse<IncomeReport>>(API_ENDPOINTS.ACCOUNTING.INCOME_REPORT, {
      params,
    })
    return unwrapResource(response.data)
  },

  incomeReportCsv: async (params: IncomeReportParams = {}): Promise<Blob> => {
    const response = await apiClient.get<Blob>(API_ENDPOINTS.ACCOUNTING.INCOME_REPORT_CSV, {
      params,
      responseType: 'blob',
    })
    return response.data
  },

  summary: async (params: { from?: string; to?: string } = {}): Promise<AccountingSummary> => {
    const response = await apiClient.get<ApiResponse<AccountingSummary>>(API_ENDPOINTS.ACCOUNTING.SUMMARY, {
      params,
    })
    return unwrapResource(response.data)
  },
}
